import React from 'lib/react';
import {
  EVGrid,
} from 'lib/components';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import TextInputField from 'commonComponents/textInputField';
import {
  expandPanel, updateClaimInfo,
} from 'features/finalizeOrder/FinalizeOrder.actions';
import DatePicker from 'commonComponents/datePicker';
import {
  showInsuredNameField, showJobNumberField, orderHasCarrierEstimate, showRenewalDateField,
} from 'features/finalizeOrder/FinalizeOrder.utils';
import OrderSummaryStyles from './ClaimInformationStyles';
import FinalizeOrderExpansionPanel from '../FinalizeOrderExpansionPanel.component';

const getPanelHeader = (header) => (<EVGrid>{header}</EVGrid>);

function ClaimInformation() {
  const classes = OrderSummaryStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    claimInfoErrors, isExpanded, claimInfo, validatorFields, selectedPrimaryProduct, controlStates, productSets, productsList,
  } = useSelector(({
    finalizeOrder, order, placeOrder, userData: { userProducts, ...userData },
  }) => ({
    ...finalizeOrder,
    productsList: userProducts[order.propertyType]?.PrimaryProducts,
    selectedPrimaryProduct: order.selectedPrimaryProduct,
    productSets: placeOrder.productSets,
    controlStates: userData.controlStates,
  }));

  const inputLabelProps = {
    className: classes.label,
    classes: { asterisk: classes.asterisk },
  };

  const inputFieldProps = {
    fullWidth: true,
    classes: { root: classes.cardInputText },
    FormHelperTextProps: { classes: { error: classes.cardInputHelperText } },
  };

  const handleChange = (fieldId, event) => {
    const claimInfoVal = { ...claimInfo };
    const { value } = event.target;
    const targetId = fieldId || event.target.id;

    claimInfoVal[targetId] = value;
    dispatch(updateClaimInfo(claimInfoVal));
  };

  const handleDateChange = (fieldId) => (date) => {
    const claimInfoVal = { ...claimInfo };
    claimInfoVal[fieldId] = date;
    dispatch(updateClaimInfo(claimInfoVal));
  };

  const displayInsuredName = showInsuredNameField(controlStates, productSets);
  const displayHomeowner = controlStates?.homeOwner && !controlStates?.homeOwner.hidden;

  return (
    <FinalizeOrderExpansionPanel
      panelSummary={getPanelHeader(t('finalize_order__claim_information'))}
      expanded={isExpanded.claimInformation}
      onChange={(_, expanded) => dispatch(expandPanel('claimInformation', expanded))}
    >
      <EVGrid className={classes.fieldContainer} item xs={12} sm={12} md={12} lg={12}>
        <TextInputField
          inputId="claimNumber"
          inputValue={claimInfo.claimNumber}
          size="small"
          handleChange={(event) => handleChange('claimNumber', event)}
          inputLabelProps={{
            ...inputLabelProps,
            required: validatorFields?.claimNumber?.required,
            error: claimInfoErrors.claimNumber.length > 0,
          }}
          inputFieldProps={inputFieldProps}
          labelText={t('finalize_order__claim_information__claimnumber')}
          helperText={claimInfoErrors.claimNumber}
        />
      </EVGrid>
      {(displayInsuredName || displayHomeowner) && (
        <EVGrid className={classes.fieldContainer} item xs={12} sm={12} md={12} lg={12}>
          <TextInputField
            inputId="insuredName"
            inputValue={claimInfo.insuredName}
            size="small"
            handleChange={(event) => handleChange('insuredName', event)}
            inputLabelProps={{
              ...inputLabelProps,
              required: displayHomeowner || !orderHasCarrierEstimate(productSets),
              error: claimInfoErrors.insuredName.length > 0,
            }}
            inputFieldProps={{
              ...inputFieldProps,
              inputProps: { maxLength: 100 },
            }}
            labelText={t(displayHomeowner ? 'finalize_order__claim_information__homeowner' : 'finalize_order__claim_information__insuredname')}
            helperText={claimInfoErrors.insuredName}
          />
        </EVGrid>
      )}
      {showJobNumberField(controlStates, selectedPrimaryProduct) && (
        <EVGrid className={classes.fieldContainer} item xs={12} sm={12} md={12} lg={12}>
          <TextInputField
            inputId="externalReferenceId"
            inputValue={claimInfo.externalReferenceId}
            size="small"
            handleChange={(event) => handleChange('externalReferenceId', event)}
            inputLabelProps={{
              ...inputLabelProps,
              required: validatorFields?.externalReferenceId?.required,
              error: claimInfoErrors.externalReferenceId.length > 0,
            }}
            inputFieldProps={{
              ...inputFieldProps,
              inputProps: { maxLength: 100 },
            }}
            labelText={t('finalize_order__claim_information__externalreferenceid')}
            helperText={claimInfoErrors.externalReferenceId}
          />
        </EVGrid>
      )}
      {(!controlStates?.claimInformation || !controlStates.claimInformation.hidden) && (
      <EVGrid className={classes.fieldContainer} item xs={12} sm={12} md={12} lg={12}>
        <TextInputField
          inputId="claimInfo"
          inputValue={claimInfo.claimInfo}
          size="small"
          handleChange={(event) => handleChange('claimInfo', event)}
          inputLabelProps={{
            ...inputLabelProps,
            required: validatorFields?.claimInfo?.required,
            error: claimInfoErrors.claimInfo.length > 0,
          }}
          inputFieldProps={inputFieldProps}
          labelText={t('finalize_order__claim_information__claiminfo')}
          helperText={claimInfoErrors.claimInfo}
        />
      </EVGrid>
      )}
      <EVGrid className={classes.fieldContainer} item xs={12} sm={12} md={12} lg={12}>
        <TextInputField
          inputId="PONumber"
          inputValue={claimInfo.PONumber}
          size="small"
          handleChange={(event) => handleChange('PONumber', event)}
          inputLabelProps={{
            ...inputLabelProps,
            required: validatorFields?.PONumber?.required,
            error: claimInfoErrors.PONumber.length > 0,
          }}
          inputFieldProps={{
            ...inputFieldProps,
            inputProps: { maxLength: 45 },
          }}
          labelText={t('finalize_order__claim_information__ponumber')}
          helperText={claimInfoErrors.PONumber}
        />
      </EVGrid>
      {
        orderHasCarrierEstimate(productSets) && (
          <EVGrid container style={{ columnGap: '24px' }}>
            <EVGrid className={classes.fieldContainer} style={{ flex: 1 }}>
              <TextInputField
                inputId="typeOfLoss"
                inputValue={claimInfo.typeOfLoss}
                size="small"
                handleChange={(event) => handleChange('typeOfLoss', event)}
                inputLabelProps={{
                  ...inputLabelProps,
                  required: false,
                  error: claimInfoErrors.typeOfLoss > 0,
                }}
                inputFieldProps={{
                  ...inputFieldProps,
                  inputProps: { maxLength: 100 },
                }}
                labelText={t('finalize_order__claim_information__typeofloss')}
                helperText={claimInfoErrors.typeOfLoss}
              />
            </EVGrid>
            <EVGrid className={classes.fieldContainer} style={{ flex: 1 }}>
              <TextInputField
                inputId="deductible"
                inputValue={claimInfo.deductible}
                size="small"
                handleChange={(event) => handleChange('deductible', event)}
                inputLabelProps={{
                  ...inputLabelProps,
                  required: false,
                  error: claimInfoErrors.deductible > 0,
                }}
                inputFieldProps={{
                  ...inputFieldProps,
                  inputProps: { maxLength: 50 },
                }}
                labelText={t('finalize_order__claim_information__deductible')}
                helperText={claimInfoErrors.deductible}
              />
            </EVGrid>
          </EVGrid>
        )
      }
      <EVGrid container style={{ columnGap: '24px' }}>
        <EVGrid className={classes.fieldContainer} style={{ flex: 1 }}>
          <DatePicker
            inputId="dateOfLoss"
            inputValue={claimInfo.dateOfLoss}
            size="small"
            handleChange={handleDateChange('dateOfLoss')}
            inputLabelProps={{
              ...inputLabelProps,
              required: validatorFields?.dateOfLoss?.required,
              error: claimInfoErrors.dateOfLoss.length > 0,
            }}
            inputFieldProps={inputFieldProps}
            labelText={t('finalize_order__claim_information__dateofloss')}
            helperText={claimInfoErrors.dateOfLoss}
          />
        </EVGrid>
        <EVGrid className={classes.fieldContainer} style={{ flex: 1 }} />
      </EVGrid>
      <EVGrid className={classes.fieldContainer} item xs={12} sm={12} md={12} lg={12}>
        <TextInputField
          inputId="catId"
          inputValue={claimInfo.catId}
          size="small"
          handleChange={(event) => handleChange('catId', event)}
          inputLabelProps={{
            ...inputLabelProps,
            required: validatorFields?.catId?.required,
            error: claimInfoErrors.catId > 0,
          }}
          inputFieldProps={{
            ...inputFieldProps,
            inputProps: { maxLength: 50 },
          }}
          labelText={t('finalize_order__claim_information__catidr')}
          helperText={claimInfoErrors.catId}
        />
      </EVGrid>
      {(controlStates?.appointmentDate && !controlStates.appointmentDate.hidden)
        && (
        <EVGrid container style={{ columnGap: '24px' }}>
          <EVGrid className={classes.fieldContainer} style={{ flex: 1 }}>
            <DatePicker
              inputId="appointmentDate"
              inputValue={claimInfo.appointmentDate}
              size="small"
              handleChange={handleDateChange('appointmentDate')}
              inputLabelProps={{
                ...inputLabelProps,
                required: false,
                error: claimInfoErrors.appointmentDate.length > 0,
              }}
              inputFieldProps={inputFieldProps}
              labelText={t('finalize_order__claim_information__appointmentdate')}
              helperText={claimInfoErrors.appointmentDate}
            />
          </EVGrid>
          <EVGrid className={classes.fieldContainer} style={{ flex: 1 }} />
        </EVGrid>
        )}
      {(showRenewalDateField(productsList, productSets) || orderHasCarrierEstimate(productSets)) && (
        <EVGrid container style={{ columnGap: 24 }}>
          <EVGrid className={classes.fieldContainer} style={{ flex: 1 }}>
            <TextInputField
              inputId="policyNumber"
              inputValue={claimInfo.policyNumber}
              size="small"
              handleChange={(event) => handleChange('policyNumber', event)}
              inputLabelProps={{
                ...inputLabelProps,
                required: false,
                error: claimInfoErrors.policyNumber > 0,
              }}
              inputFieldProps={{
                ...inputFieldProps,
                inputProps: { maxLength: 100 },
              }}
              labelText={t('finalize_order__claim_information__policynumber')}
              helperText={claimInfoErrors.policyNumber}
            />
          </EVGrid>
          <EVGrid className={classes.fieldContainer} style={{ flex: 1 }}>
            {
            showRenewalDateField(productsList, productSets) && (
            <DatePicker
              inputId="renewalDate"
              inputValue={claimInfo.renewalDate}
              size="small"
              handleChange={handleDateChange('renewalDate')}
              inputLabelProps={{
                ...inputLabelProps,
                required: false,
                error: claimInfoErrors.renewalDate.length > 0,
              }}
              inputFieldProps={inputFieldProps}
              labelText={t('finalize_order__claim_information__renewaldate')}
              helperText={claimInfoErrors.renewalDate}
            />
            )
              }
          </EVGrid>
        </EVGrid>
      )}
    </FinalizeOrderExpansionPanel>
  );
}

export default ClaimInformation;
